/**
 * @typedef  {Object} CollectionItem The collection item
 * @property {String} name The item's name
 * @property {Body}   item The item
 */

/**
 * Provides a list for getting, setting, sorting, & removing entities by name
 * @class Collection
 */
export default class Collection {
    constructor() {
        this._items = [];
    }

    /**
     * Returns the item CollectionItem object
     * @method Collection#_getRawItem
     * @param  {String} name
     * @return {CollectionItem}
     * @private
     */
    _getRawItem(name) {
        let item;

        this._rawEach(function(iterItem, i, iterName) {
            if (name === iterName) {
                item = iterItem;

                return false;
            }
        });

        return item;
    }

    /**
     * Iterates the collection's sorted items. The raw item, index, name, and the
     * list being iterated are supplied to the provided function
     * @method Collection#_rawEach
     * @param {Function} fn
     * @private
     */
    _rawEach(fn) {
        for(var i = 0, len = this._items.length; i < len; i += 1) {
            if (fn(this._items[i], i, this._items[i].name, this._items) === false) {
                break;
            }
        }
    }

    /**
     * Add an item with optional name
     * @method Collection#addItem
     * @param  {Any}        item   The item to add
     * @param  {String}     [name] The optional name of the item
     * @return {Collection}
     */
    addItem(item, name) {
        name = name || "";

        this._items.push({
            item, name
        });

        return this;
    }

    /**
     * Add multiple items
     * @method Collection#addItems
     * @param  {(...CollectionItem|Body)} items Can be the object itself or an object containing the entity and its name
     * @return {Collection}
     */
    addItems(...items) {
        for (let item of items) {
            if (typeof item.item === "object" && typeof item.name === "string") {
                // if item has item/name structure
                this.addItem(item.item, item.name);
            } else {
                // for convenience allow user to add just item
                this.addItem(item);
            }
        }

        return this;
    }

    /**
     * Iterates the collection's sorted items. The item, index, and name are supplied
     * to the provided function
     * @method Collection#each
     * @param {Function} fn      The function to execute on the iterable
     * @param {Object}   [scope] The scope with which to execute the function
     */
    each(fn, scope) {
        fn = scope ? fn.bind(scope) : fn;

        for (var i = 0, len = this._items.length; i < len; i++) {
            let item = this._items[i],
                doContinue;

            // if item on last item and an item is removed
            if (!item) {
                break;
            }

            doContinue = fn(item.item, i, item.name);

            if (doContinue === false) {
                break;
            }
        }
    }

    /**
     * iterates items and return the ones that meet criteria
     * @method Collection#filter
     * @param  {Function} fn      Truth predicate
     * @param  {Object}   [scope] The scope in which to execute the function
     * @return {Array}
     */
    filter(fn, scope) {
        let filteredItems = [];

        this.each((item, i, name)=> {
            let predicate = fn(item, i, name);

            if (predicate) {
                filteredItems.push(item);
            }
        }, scope);

        return filteredItems;
    }

    /**
     * Returns a list of just the items
     * @method Collection#getArray
     * @return {Array}
     */
    getArray() {
        return this._items.map((item)=> {
            return item.item;
        });
    }

    /**
     * Returns an existing item by name, or undefined if the name is not found
     *
     * @param  {String} name The name of the item
     * @return {(Body|undefined)}
     */
    getItem(name) {
        let item;

        this.each((iterItem, i, iterName)=> {
            if (name === iterName) {
                item = iterItem;

                return false;
            }
        });

        return item;
    }

    /**
     * Returns an existing item by index
     *
     * @param  {Integer} index
     * @return {Body}
     */
    getAt(index) {
        return this._items[index].item;
    }

    /**
     * Returns the count of items in collection
     *
     * @return {Integer}
     */
    getCount() {
        return this._items.length;
    }

    /**
     * Returns an item's current index
     *
     * @param  {String} name
     * @return {Integer}
     */
    getIndexWithName(name) {
        let index;

        this.each((iterItem, i, iterName)=> {
            if (name === iterName) {
                index = i;

                return false;
            }
        });

        return index;
    }

    /**
     * Removes all items from collection
     */
    removeAll () {
        this._items = [];
    }

    /**
     * Removes an object by name
     * @method Collection#removeWithName
     * @param  {String}  name
     * @return {Boolean} Returns true if item removed, false if not
     */
    removeWithName (name) {
        let removed = false;

        this._rawEach((iterItem, i, iterName, items)=> {
            if (name === iterName) {
                iterItem = null;
                items.splice(i, 1);
                removed = true;

                // break out of loop
                return false;
            }
        });

        return removed;
    }

    remove (item) {
        let removed = false;

        this._rawEach((iterItem, i, iterName, items)=> {
            if (item.uuid === iterItem.item.uuid) {
                iterItem = null;
                items.splice(i, 1);
                removed = true;

                // break out of loop
                return false;
            }
        });

        return removed;
    }

    /**
     * Assigns a new value to an existing item
     * @param {String} name  The name of the object to modify
     * @param {Any}    value The new value
     */
    update (name, value) {
        this._rawEach((iterItem, i, iterName)=> {
            if (name === iterName) {
                iterItem.item = value;

                // break out of loop
                return false;
            }
        });
    }

    /**
     * Moves item to new index
     * @param {String}  name  The name of the object being moved
     * @param {Integer} index The item's new index
     */
    setIndex(name, index) {
        let item;
        let currentIndex = this.getIndexWithName(name);

        if (index === currentIndex) {
            return;
        }

        item = this._getRawItem(name);
        this.removeWithName(name);
        this._items.splice(index, 0, item);
    }
}



/** WEBPACK FOOTER **
 ** ./src/Collection.js
 **/